(() => {
  if (customElements.get('sa-product-sticky-bar')) return;

  class SaProductStickyBar extends HTMLElement {
    connectedCallback() {
      this.mainSectionId = this.dataset.mainSectionId?.trim() || this.resolveMainSectionId();
      this.scrollThreshold = Number(this.dataset.scrollThreshold) || 0;
      this.triggerElement = null;
      this.observer = null;
      this.scrollTicking = false;
      this.mediaPlaying = false;
      this.mediaSyncQueued = false;
      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onProductInfoLoaded = this.onProductInfoLoaded.bind(this);
      this.onMediaPlayback = this.onMediaPlayback.bind(this);
      this.onProductMediaClick = this.onProductMediaClick.bind(this);
      this.onProductMediaPaused = this.onProductMediaPaused.bind(this);

      this.applyMainSectionBindings();
      this.initTrigger();
      this.bindAddToCart();
      this.bindVariantSync();
      this.bindZoom();
      this.bindMediaPlayback();
      document.addEventListener('product-info:loaded', this.onProductInfoLoaded);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();
    }

    applyMainSectionBindings() {
      if (!this.mainSectionId) return;

      this.dataset.mainSectionId = this.mainSectionId;

      this.querySelectorAll('[data-sa-sticky-buy-now]').forEach((buyNow) => {
        buyNow.dataset.productFormId = `product-form-${this.mainSectionId}`;
        buyNow.dataset.sectionId = this.mainSectionId;
      });

      this.querySelectorAll('[data-sa-sticky-add-to-cart]').forEach((addToCart) => {
        addToCart.dataset.mainSectionId = this.mainSectionId;
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('product-info:loaded', this.onProductInfoLoaded);
      document.removeEventListener('play', this.onMediaPlayback, true);
      document.removeEventListener('pause', this.onMediaPlayback, true);
      document.removeEventListener('ended', this.onMediaPlayback, true);
      document.removeEventListener('click', this.onProductMediaClick);
      document.removeEventListener('sa:product-media:paused', this.onProductMediaPaused);
      this.observer?.disconnect();
      this.variantUnsubscriber?.();
      this.optionUnsubscriber?.();
      this.closeZoom();
    }

    resolveMainSectionId() {
      const productInfo = document.querySelector('product-info[data-section]');
      return productInfo?.dataset.section || '';
    }

    getProductInfo() {
      if (this.mainSectionId) {
        return (
          document.querySelector(`product-info[data-section="${this.mainSectionId}"]`) ||
          document.querySelector(`#MainProduct-${this.mainSectionId}`)
        );
      }

      return document.querySelector('product-info[data-section]');
    }

    isDesktop() {
      return window.matchMedia('(min-width: 1000px)').matches;
    }

    initTrigger() {
      const sectionId = this.mainSectionId;
      this.triggerElement =
        document.querySelector('.sa-product-buy-buttons__actions') ||
        (sectionId ? document.getElementById(`ProductSubmitButton-${sectionId}`) : null) ||
        document.querySelector('[data-sa-sticky-bar-trigger]') ||
        document.querySelector('product-info .product-form__buttons');

      if (!this.triggerElement || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        () => {
          this.onScroll();
        },
        {
          root: null,
          threshold: [0, 1],
        },
      );

      this.observer.observe(this.triggerElement);
    }

    bindAddToCart() {
      this.querySelectorAll('[data-sa-sticky-add-to-cart]').forEach((button) => {
        button.addEventListener('click', () => {
          const sectionId = this.mainSectionId;
          const submitButton = sectionId ? document.getElementById(`ProductSubmitButton-${sectionId}`) : null;
          if (submitButton && !submitButton.disabled) {
            submitButton.click();
          }
        });
      });
    }

    bindVariantSync() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
        requestAnimationFrame(() => this.bindVariantSync());
        return;
      }

      if (!this.variantUnsubscriber) {
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
          const { sectionId, variant, html } = data || {};
          if (!this.isMatchingSection(sectionId)) return;

          this.updatePrice(html, sectionId, variant);
          this.updateTitle(html, sectionId);
          this.updateImage(variant, html, sectionId);
          this.syncButtons(sectionId, variant);
        });
      }

      if (!this.optionUnsubscriber && PUB_SUB_EVENTS.optionValueSelectionChange) {
        this.optionUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data } = {}) => {
          const { event, target } = data || {};
          const productInfo = this.getProductInfo();
          if (!productInfo || !event?.target || !productInfo.contains(event.target)) return;

          const sectionId = productInfo.dataset.section || this.mainSectionId;
          if (!this.isMatchingSection(sectionId)) return;

          // Instantly mirror the clicked Color / variant-image swatch.
          const fromTarget = this.extractImageFromOptionTarget(target);
          if (fromTarget?.src) {
            this.applyImage(fromTarget.src, fromTarget.alt);
            return;
          }

          const fromSwatch = this.extractImageFromSwatch(null, sectionId);
          if (fromSwatch?.src) {
            this.applyImage(fromSwatch.src, fromSwatch.alt);
          }
        });
      }
    }

    onProductInfoLoaded(event) {
      const productInfo = event?.target;
      if (!(productInfo instanceof HTMLElement) || productInfo.tagName !== 'PRODUCT-INFO') return;

      const sectionId = productInfo.dataset.section;
      if (!this.isMatchingSection(sectionId)) return;

      this.mainSectionId = sectionId || this.mainSectionId;
      this.applyMainSectionBindings();
      this.syncFromProductInfo(productInfo);
    }

    syncFromProductInfo(productInfo = this.getProductInfo()) {
      if (!productInfo) return;

      const sectionId = productInfo.dataset.section || this.mainSectionId;
      const variantSelects = productInfo.querySelector('variant-selects:not([style*="display: none"])') ||
        productInfo.querySelector('variant-selects');
      let variant = null;

      try {
        variant = JSON.parse(variantSelects?.querySelector('[data-selected-variant]')?.textContent || 'null');
      } catch (error) {
        variant = null;
      }

      this.updatePrice(null, sectionId, variant);
      this.updateTitle(productInfo, sectionId);
      this.updateImage(variant, productInfo, sectionId);
      this.syncButtons(sectionId, variant);
    }

    isMatchingSection(sectionId) {
      if (!sectionId) return false;

      const resolved = this.resolveMainSectionId();
      if (!this.dataset.mainSectionId?.trim()) {
        this.mainSectionId = resolved;
        this.applyMainSectionBindings();
      }

      return !this.mainSectionId || sectionId === this.mainSectionId || sectionId === resolved;
    }

    bindZoom() {
      this.zoomModal = this.querySelector('[data-sa-sticky-zoom-modal]');
      this.zoomImage = this.querySelector('[data-sa-sticky-zoom-image]');
      this.zoomOpenBtn = this.querySelector('[data-sa-sticky-zoom-open]');

      this.zoomOpenBtn?.addEventListener('click', () => {
        this.openZoom();
      });

      this.querySelector('[data-sa-sticky-zoom-close]')?.addEventListener('click', () => {
        this.closeZoom();
      });

      this.zoomModal?.addEventListener('click', (event) => {
        if (event.target === this.zoomModal) {
          this.closeZoom();
        }
      });
    }

    openZoom() {
      if (!this.zoomModal || this.isDesktop()) return;

      const src = this.zoomOpenBtn?.dataset.zoomSrc || this.zoomImage?.src;
      const alt = this.zoomOpenBtn?.dataset.zoomAlt || this.zoomImage?.alt || '';
      if (!src) return;

      if (this.zoomImage) {
        this.zoomImage.src = src;
        this.zoomImage.alt = alt;
      }

      this.zoomModal.hidden = false;
      this.zoomModal.classList.add('is-open');
      this.zoomModal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);
    }

    closeZoom() {
      if (!this.zoomModal) return;

      this.zoomModal.classList.remove('is-open');
      this.zoomModal.hidden = true;
      this.zoomModal.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', this.onKeydown);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.closeZoom();
      }
    }

    updateTitle(scope, sectionId) {
      const roots = [scope, document].filter(Boolean);
      let title = '';

      for (const root of roots) {
        title =
          root.querySelector?.(`#ProductInfo-${sectionId} .product__title`)?.textContent?.trim() ||
          root.querySelector?.('.sa-product-info .product__title')?.textContent?.trim() ||
          root.querySelector?.('variant-selects[data-product-title]')?.dataset?.productTitle ||
          root.dataset?.productTitle ||
          '';
        if (title) break;
      }

      if (!title) return;

      this.querySelectorAll('.sa-product-sticky-bar__title').forEach((node) => {
        node.textContent = title;
      });
    }

    updatePrice(html, sectionId, variant) {
      this.querySelectorAll('[data-sa-sticky-price]').forEach((priceRoot) => {
        const source =
          html?.getElementById?.(`price-${sectionId}`) ||
          html?.querySelector?.(`#price-${sectionId}`) ||
          document.getElementById(`price-${sectionId}`);

        const saleEl = priceRoot.querySelector('.sa-product-sticky-bar__price-sale');
        const compareWrap = priceRoot.querySelector('[data-sa-sticky-compare-wrap]');
        const compareEl = priceRoot.querySelector('.sa-product-sticky-bar__price-compare');
        const badgeEl = priceRoot.querySelector('[data-sa-sticky-badge]');

        if (source) {
          const saleText = source.querySelector('.price-item--sale')?.textContent?.trim();
          const compareText = source.querySelector('s')?.textContent?.trim();
          const badgeText = Array.from(source.querySelectorAll('span')).find((node) =>
            /% OFF/.test(node.textContent || ''),
          )?.textContent?.trim();

          if (saleEl && saleText) saleEl.textContent = saleText;
          if (compareEl && compareText) compareEl.textContent = compareText;

          const onSale = Boolean(compareText && badgeText);
          compareWrap?.classList.toggle('hidden', !onSale);
          badgeEl?.classList.toggle('hidden', !onSale);
          if (badgeEl && badgeText) badgeEl.textContent = badgeText;
          priceRoot.classList.toggle('sa-product-sticky-bar__price--no-sale', !onSale);
          return;
        }

        if (!variant) return;

        if (saleEl && typeof variant.price === 'number') {
          saleEl.textContent = this.formatMoney(variant.price);
        }

        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        compareWrap?.classList.toggle('hidden', !onSale);
        badgeEl?.classList.toggle('hidden', !onSale);
        priceRoot.classList.toggle('sa-product-sticky-bar__price--no-sale', !onSale);

        if (compareEl && onSale) {
          compareEl.textContent = this.formatMoney(variant.compare_at_price);
        }

        if (badgeEl && onSale) {
          const discount = Math.round(
            ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price,
          );
          badgeEl.textContent = `${discount}% OFF`;
        }
      });
    }

    getVariantImage(variant, html, sectionId) {
      // Prefer the selected Color / variant-image swatch — matches what the shopper just clicked.
      const fromSwatch = this.extractImageFromSwatch(html, sectionId);
      if (fromSwatch?.src) return fromSwatch;

      const fromVariant = this.extractImageFromVariant(variant);
      if (fromVariant?.src) return fromVariant;

      const fromGallery = this.extractImageFromGallery(variant, html);
      if (fromGallery?.src) return fromGallery;

      return null;
    }

    extractImageFromVariant(variant) {
      if (!variant) return null;

      const featuredImage = variant.featured_image;
      if (featuredImage?.src) {
        return {
          src: featuredImage.src,
          alt: featuredImage.alt || '',
        };
      }

      const featuredMedia = variant.featured_media;
      if (!featuredMedia) return null;

      const preview = featuredMedia.preview_image || featuredMedia.image || featuredMedia;
      const src = preview?.src || preview?.url || featuredMedia.src || featuredMedia.url;
      if (!src) return null;

      return {
        src,
        alt: preview?.alt || featuredMedia.alt || '',
      };
    }

    extractImageFromOptionTarget(target) {
      if (!target) return null;

      const swatch = target.closest?.('.sa-product-variant-picker__swatch');
      if (!swatch) return null;

      const label =
        (target.id && swatch.querySelector(`label[for="${CSS.escape(target.id)}"]`)) ||
        swatch.querySelector('label') ||
        target.nextElementSibling;

      const img = label?.querySelector?.('img');
      const src = img?.currentSrc || img?.getAttribute?.('src');
      if (src) {
        return {
          src,
          alt: img.getAttribute('alt') || target.value || '',
        };
      }

      const swatchEl = label?.querySelector?.('.swatch');
      const bg = swatchEl?.style?.getPropertyValue('--swatch--background') || '';
      const bgSrc = bg.replace(/^url\(["']?/, '').replace(/["']?\)$/, '');
      if (bgSrc && bgSrc !== 'none') {
        return {
          src: bgSrc,
          alt: target.value || '',
        };
      }

      return null;
    }

    getVisibleVariantSelects(scope, sectionId) {
      const candidates = [];

      if (sectionId) {
        candidates.push(
          scope?.querySelector?.(`#variant-selects-${sectionId}`),
          document.getElementById(`variant-selects-${sectionId}`),
        );
      }

      candidates.push(
        scope?.querySelector?.('variant-selects.sa-product-variant-picker'),
        ...document.querySelectorAll('variant-selects.sa-product-variant-picker'),
      );

      return candidates.filter((node) => {
        if (!node) return false;
        if (node.style?.display === 'none') return false;
        return true;
      });
    }

    extractImageFromSwatch(html, sectionId) {
      const roots = this.getVisibleVariantSelects(html, sectionId);

      for (const root of roots) {
        const checkedInput = root.querySelector(
          '.sa-product-variant-picker__swatch input:checked, .sa-product-variant-picker__swatch--color input:checked',
        );
        if (!checkedInput) continue;

        const imageData = this.extractImageFromOptionTarget(checkedInput);
        if (imageData?.src) return imageData;
      }

      return null;
    }

    extractImageFromGallery(variant, html) {
      const mediaId = variant?.featured_media?.id || variant?.featured_image?.id;
      if (!mediaId) return null;

      const scopes = [html, document].filter(Boolean);

      for (const scope of scopes) {
        const mediaNode = scope.querySelector?.(
          `sa-product-media-gallery [data-media-id="${mediaId}"] img, [data-media-id="${mediaId}"] img`,
        );
        if (mediaNode?.src) {
          return {
            src: mediaNode.currentSrc || mediaNode.src,
            alt: mediaNode.alt || '',
          };
        }
      }

      return null;
    }

    buildImageUrl(src, width) {
      if (!src) return '';

      const normalized = src.startsWith('//') ? `https:${src}` : src;

      try {
        const url = new URL(normalized, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch (error) {
        if (normalized.includes('?')) {
          return `${normalized}&width=${width}`;
        }
        return `${normalized}?width=${width}`;
      }
    }

    applyImage(src, alt = '') {
      if (!src) return;

      const thumbSrc = this.buildImageUrl(src, 140);
      const zoomSrc = this.buildImageUrl(src, 1600);

      this.querySelectorAll('.sa-product-sticky-bar__image').forEach((image) => {
        if (image.tagName === 'IMG') {
          image.src = thumbSrc;
          image.srcset = `${thumbSrc} 140w`;
          image.removeAttribute('loading');
          if (alt) {
            image.alt = alt;
          }
        } else {
          image.style.backgroundImage = `url("${thumbSrc}")`;
        }
      });

      if (this.zoomOpenBtn) {
        this.zoomOpenBtn.dataset.zoomSrc = zoomSrc;
        this.zoomOpenBtn.dataset.zoomAlt = alt || '';
        this.zoomOpenBtn.hidden = false;
      }

      if (this.zoomImage) {
        this.zoomImage.src = zoomSrc;
        this.zoomImage.alt = alt || '';
      }
    }

    updateImage(variant, html, sectionId) {
      const imageData = this.getVariantImage(variant, html, sectionId);
      if (!imageData?.src) return;
      this.applyImage(imageData.src, imageData.alt || '');
    }

    syncButtons(sectionId, variant) {
      const submitButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
      const disabled = submitButton?.disabled ?? !(variant?.available ?? false);

      this.querySelectorAll('[data-sa-sticky-add-to-cart], [data-sa-sticky-buy-now]').forEach((button) => {
        button.disabled = disabled;
      });
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        const format =
          window.theme?.moneyFormat ||
          window.Shopify.theme?.moneyFormat ||
          '${{amount}}';
        return Shopify.formatMoney(cents, format);
      }

      return `$${(cents / 100).toFixed(2)}`;
    }

    isPastTrigger() {
      if (!this.triggerElement) {
        return window.scrollY > this.scrollThreshold + 120;
      }

      const rect = this.triggerElement.getBoundingClientRect();
      const stickyTop = this.getStickyTop();
      return rect.bottom <= stickyTop + this.scrollThreshold;
    }

    getStickyTop() {
      const styles = getComputedStyle(document.documentElement);
      const announcement = parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 0;
      const header = parseFloat(styles.getPropertyValue('--header-height')) || 0;
      return announcement + header;
    }

    bindMediaPlayback() {
      document.addEventListener('play', this.onMediaPlayback, true);
      document.addEventListener('pause', this.onMediaPlayback, true);
      document.addEventListener('ended', this.onMediaPlayback, true);
      document.addEventListener('click', this.onProductMediaClick);
      document.addEventListener('sa:product-media:paused', this.onProductMediaPaused);
    }

    onMediaPlayback(event) {
      if (!(event.target instanceof HTMLVideoElement)) return;
      if (!this.isProductDetailMedia(event.target)) return;
      this.scheduleMediaSync();
    }

    onProductMediaClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        !target.closest(
          '.deferred-media__poster, [data-sa-media-banner-play], [data-sa-thumb-index], [data-sa-viewer-nav], [data-sa-modal-thumb-index], [data-sa-modal-close]',
        )
        ) {
        return;
      }
      this.scheduleMediaSync();
      if (target.closest('.deferred-media__poster, [data-sa-media-banner-play]')) {
        window.setTimeout(() => this.scheduleMediaSync(), 400);
      }
    }

    onProductMediaPaused() {
      this.scheduleMediaSync();
    }

    scheduleMediaSync() {
      if (this.mediaSyncQueued) return;
      this.mediaSyncQueued = true;
      window.setTimeout(() => {
        this.mediaSyncQueued = false;
        const playing = this.hasPlayingProductMedia();
        if (playing === this.mediaPlaying) return;
        this.mediaPlaying = playing;
        this.onScroll();
      }, 0);
    }

    isProductDetailMedia(node) {
      return !!node.closest(
        'sa-product-media-gallery, .sa-product-media-gallery__modal, .sa-product-media-banner',
      );
    }

    isCurrentProductMedia(node) {
      const modal = node.closest('.sa-product-media-gallery__modal');
      if (modal?.hasAttribute('hidden')) return false;

      const slide = node.closest(
        '.sa-product-media-gallery__slide, .sa-product-media-gallery__modal-slide, .sa-product-media-gallery__grid-item',
      );
      if (!slide) return true;
      return slide.classList.contains('is-active');
    }

    hasPlayingProductMedia() {
      const videos = document.querySelectorAll(
        'sa-product-media-gallery video, .sa-product-media-gallery__modal video, .sa-product-media-banner video',
      );
      for (const video of videos) {
        if (!video.paused && !video.ended) return true;
      }

      const iframes = document.querySelectorAll(
        'sa-product-media-gallery deferred-media[loaded] iframe, .sa-product-media-gallery__modal deferred-media[loaded] iframe',
      );
      for (const iframe of iframes) {
        if (this.isCurrentProductMedia(iframe)) return true;
      }

      const inserts = document.querySelectorAll('.sa-product-media-banner video-intersection-insert');
      for (const insert of inserts) {
        if (insert._isPlaying) return true;
      }

      return false;
    }

    syncStickyOffset(visible) {
      const stickyBarHeight = visible ? this.offsetHeight || 0 : 0;

      document.documentElement.style.setProperty('--sa-product-sticky-bar-height', `${stickyBarHeight}px`);
      document.dispatchEvent(new CustomEvent('sa:sticky-offset:updated'));
    }

    setVisible(visible) {
      const show = visible && !this.mediaPlaying;
      this.classList.toggle('is-visible', show);
      this.classList.toggle('is-hidden-for-media', Boolean(this.mediaPlaying));
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.syncStickyOffset(show);
    }

    onScrollDesktop() {
      this.setVisible(this.isPastTrigger());
    }

    onScrollMobile() {
      this.setVisible(true);
    }

    onScroll() {
      if (this.scrollTicking) return;

      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.scrollTicking = false;

        if (this.isDesktop()) {
          this.onScrollDesktop();
          return;
        }

        this.onScrollMobile();
      });
    }
  }

  customElements.define('sa-product-sticky-bar', SaProductStickyBar);
})();
